import { Schema } from 'prosemirror-model'
import { Plugin, TextSelection, Transaction } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { deleteCellContents } from '../../Commands/table/mutation'
import styles from '../../nodeViews/Table/Table.module.scss'
import { getPreviousTextPosition } from '../../utils'
import { isTableSelection } from '../../utils/table'
import { CellSelection } from '../../utils/table/CellSelection'
import { serializeTableForClipboard } from '../../utils/table/serializeTableForClipboard'
import { GapCursor } from '../GapCursor/gapCursor'
import { pasteIntoTable } from './pasteIntoTable'
import { tableSelectionPluginKey } from './tableSelectionPluginKey'
import { handleMouseDown, handleTripleClick, normalizeSelection } from './tableSelectionUtils'

export function tableSelection(schema: Schema) {
  return new Plugin({
    key: tableSelectionPluginKey,
    view: (view) => {
      const fixUnsyncedSelection = () => {
        const browserSelection = window.getSelection()
        /**
         * Workaround for a prosemirror bug where prosemirror-view adds the `hideselection` class too late (after
         * mouseUp). This causes the underlying browser text selection to be visible while dragging a CellSelection
         */
        if (
          view.state.selection instanceof CellSelection &&
          !view.dom.classList.contains('ProseMirror-hideselection')
        ) {
          // PM will remove this automatically when appropriate
          view.dom.classList.add('ProseMirror-hideselection')
        }

        if ((browserSelection?.anchorNode as HTMLElement)?.classList?.contains(styles.tableContainer)) {
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const pos = view.posAtDOM(browserSelection?.anchorNode!, 0) - 1
          if (pos < 0) return
          const $desiredGapCursorPos = view.state.doc.resolve(pos)
          let tr: Transaction = view.state.tr
          if (GapCursor.valid($desiredGapCursorPos)) {
            tr.setSelection(new GapCursor($desiredGapCursorPos))
          } else {
            tr.setSelection(new TextSelection(getPreviousTextPosition($desiredGapCursorPos)))
          }

          tr.setMeta(tableSelectionPluginKey, { shouldIgnoreNextMouseUp: true })
          view.dispatch(tr)
        }
      }

      document.addEventListener('selectionchange', fixUnsyncedSelection)
      return {
        destroy: () => {
          document.removeEventListener('selectionchange', fixUnsyncedSelection)
        },
      }
    },

    /**
     * @param anchorPosOrMouseHandlingSignal used to remember when a mouse-drag cell-selection is happening, so that it
     * can continue even as transactions (which might move its anchor cell) come in.
     * This weird usage of a variable where it sometimes contains a pos, and sometimes it contains a signal (which can
     * be null, undefined or -1) to something else indicating it should start/stop handling stuff
     * @param shouldIgnoreNextMouseUp fixUnsyncedSelection properly puts a gap cursor between two tables (or a table and
     * another block node), but prosemirror un-fixes it with an over-eager mouseUp handler in an attempt to fix a chrome
     * bug, so we "swallow" the mouseUp event (see https://github.com/ProseMirror/prosemirror-view/blob/8f246f320801f8e3cac92c97f71ac91e3e327f2f/src/input.js#L379)
     */
    state: {
      init() {
        return { anchorPosOrMouseHandlingSignal: null, shouldIgnoreNextMouseUp: false }
      },
      apply(tr, currentPluginState) {
        if (!tr.getMeta(tableSelectionPluginKey)) {
          return currentPluginState
        }
        let anchorPosOrMouseHandlingSignal: null | undefined | number =
          tr.getMeta(tableSelectionPluginKey)?.anchorPosOrMouseHandlingSignal
        const shouldIgnoreNextMouseUp: boolean | undefined =
          tr.getMeta(tableSelectionPluginKey)?.shouldIgnoreNextMouseUp
        if (shouldIgnoreNextMouseUp !== undefined) return { ...currentPluginState, shouldIgnoreNextMouseUp }
        if (anchorPosOrMouseHandlingSignal != null)
          return {
            ...currentPluginState,
            anchorPosOrMouseHandlingSignal:
              anchorPosOrMouseHandlingSignal === -1 ? null : anchorPosOrMouseHandlingSignal,
          }
        if (currentPluginState.anchorPosOrMouseHandlingSignal == null || !tr.docChanged) return currentPluginState
        let { deleted, pos } = tr.mapping.mapResult(currentPluginState.anchorPosOrMouseHandlingSignal)
        return {
          ...currentPluginState,
          ...(deleted ? { anchorPosOrMouseHandlingSignal: null } : { anchorPosOrMouseHandlingSignal: pos }),
        }
      },
    },

    props: {
      handleDOMEvents: {
        mouseup: (view, e) => {
          const shouldIgnoreNextMouseUp: boolean | undefined = tableSelectionPluginKey.getState(
            view.state,
          ).shouldIgnoreNextMouseUp
          if (shouldIgnoreNextMouseUp) {
            e.stopPropagation()

            view.dispatch(view.state.tr.setMeta(tableSelectionPluginKey, { shouldIgnoreNextMouseUp: false }))
            return true
          }
          return false
        },
        mousedown: handleMouseDown as (view: EditorView, event: Event) => boolean,
        copy: (view: EditorView, event: Event) => serializeTableForClipboard(view, event as ClipboardEvent),
        /**
         * Don't allow cutting, instead mutate the event to be a "copy" event, and allow it to
         * continue propagating (to allow other handlers to jump in) and afterwards clear the cells
         *
         * Note that we return "false" here to allow propagation to continue
         */
        cut: (view: EditorView, e: Event) => {
          if (isTableSelection(view.state.selection)) return false

          let shouldDeleteContent = true
          if (view.state.selection instanceof CellSelection) {
            shouldDeleteContent = false
            Object.defineProperty(e, 'type', { value: 'copy' })

            setTimeout(() => {
              deleteCellContents(view.state, view.dispatch)
            }, 0)
          }
          return serializeTableForClipboard(view, e as ClipboardEvent, true, shouldDeleteContent)
        },
        /**
         * If the user types or presses enter while a CellSelection is active, ignore the event
         */
        keypress: (view: EditorView, event: Event) => {
          const e: KeyboardEvent = event as any

          if (view.state.selection instanceof CellSelection) {
            if ((e.key.length === 1 && !(e.ctrlKey || e.altKey || e.metaKey)) || ['Enter'].includes(e.key)) {
              e.preventDefault()
              return true
            }
          }
          return false
        },
      },

      createSelectionBetween(view) {
        if (tableSelectionPluginKey.getState(view.state).anchorPosOrMouseHandlingSignal !== null)
          return view.state.selection
      },
      handlePaste: pasteIntoTable(schema),
      handleTripleClick,
    },

    appendTransaction(_, __, newState) {
      return normalizeSelection(newState)
    },
  })
}
